import ChevronDownIcon from '@components/Icons/ChevronDownIcon'
import ChevronUpIcon from '@components/Icons/ChevronUpIcon'
import { styled, theme } from 'twin.macro'
import { useRef, useEffect, useState } from 'react'
import ConvertKitEdm from '@components/ConvertKitEdm'

export const TABLE_OF_CONTENTS_NAME = 'table-of-contents'

type Item = {
  type: 'h2' | 'h3'
  href: string
  textContent: string
}

type Props = {
  items: Item[]
  clas: Array<string>
  postID: string
  isAmp?: boolean
}

const Container = styled.div`
  font-size: 16px;
  padding: 12px 0;
  margin: 12px 0;

  border-top: 1px solid #e6e6e6;
  border-bottom: 1px solid #e6e6e6;

  @media (min-width: ${theme`screens.md`}) {
    font-size: 18px;
  }
`

const Header = styled.button`
  height: 32px;
  display: flex;
  align-items: center;
  color: #7d7af4;
  width: 100%;

  .title {
    text-align: left;
    flex-grow: 1;
    color: #333333;
    font-weight: 600;
  }

  svg {
    margin-right: 4px;
  }

  span {
    margin-right: 12px;
    font-size: 14px;
  }
`

const List = styled.div`
  overflow: hidden;
  max-height: calc(var(--list-folded, 1) * var(--list-max-height));
  transition: max-height 500ms;
`

const Item = styled.div`
  line-height: 28px;
  display: flex;

  a.toc-anchor {
    color: inherit;
    margin-inline-end: 0;
    &:hover,
    &:active {
      text-decoration: underline;
    }
  }

  &.h2 {
    color: #433bb7;
    font-weight: 600;
    margin-bottom: 8px;
  }

  &.h3 {
    color: #6056db;
    padding-left: 24px;
    margin-bottom: 4px;
  }

  &.h3 + .h3 {
    margin-bottom: 2px;
  }

  &.h3 + .h2 {
    margin-top: 12px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &:first-child {
    margin-top: 14px;
  }

  svg {
    flex-shrink: 0;
  }
`

const H2Bullet = () => {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 13.2C10 11.4327 11.4327 10 13.2 10H14.8C16.5673 10 18 11.4327 18 13.2V14.8C18 16.5673 16.5673 18 14.8 18H13.2C11.4327 18 10 16.5673 10 14.8V13.2Z"
        fill="#433BB7"
      />
    </svg>
  )
}

const H3Bullet = () => {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 13.2C10 11.4327 11.4327 10 13.2 10H14.8C16.5673 10 18 11.4327 18 13.2V14.8C18 16.5673 16.5673 18 14.8 18H13.2C11.4327 18 10 16.5673 10 14.8V13.2Z"
        fill="#EAEAFF"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.8 10.8H13.2C11.8745 10.8 10.8 11.8745 10.8 13.2V14.8C10.8 16.1255 11.8745 17.2 13.2 17.2H14.8C16.1255 17.2 17.2 16.1255 17.2 14.8V13.2C17.2 11.8745 16.1255 10.8 14.8 10.8ZM13.2 10C11.4327 10 10 11.4327 10 13.2V14.8C10 16.5673 11.4327 18 13.2 18H14.8C16.5673 18 18 16.5673 18 14.8V13.2C18 11.4327 16.5673 10 14.8 10H13.2Z"
        fill="#7D7AF4"
      />
    </svg>
  )
}

export const TableOfContents = (props: Props) => {
  const { items, clas, postID, isAmp } = props
  const [isFolded, setIsFolded] = useState(false)
  const ref = useRef(null)
  const isForumPost = postID && postID.includes('forum.')

  useEffect(() => {
    if (!ref.current) return
    ref.current.style.setProperty('--list-max-height', ref.current.clientHeight + 'px')
  }, [])

  useEffect(() => {
    if (isFolded) return ref.current.style.setProperty('--list-folded', 0)

    ref.current.style.setProperty('--list-folded', 1)
  }, [isFolded])

  const toggleButton = isFolded ? (
    <>
      <ChevronDownIcon width="16" />
      <span>展開</span>
    </>
  ) : (
    <>
      <ChevronUpIcon width="16" />
      <span>收合</span>
    </>
  )

  const onClick = (id: string) => (e) => {
    e.preventDefault()
    const target = document.querySelector(id)
    if (!target) return
    window.scrollBy({
      top: target.getBoundingClientRect().y - window.innerHeight * 0.2,
      behavior: 'smooth',
    })
  }

  return (
    <Container>
      <Header onClick={() => setIsFolded(!isFolded)}>
        <div className="title">文章目錄</div>
        {toggleButton}
      </Header>
      <List ref={ref}>
        {items.map((item, index) => {
          return (
            <Item className={item.type} key={index}>
              {item.type === 'h2' ? <H2Bullet /> : <H3Bullet />}
              <a className="toc-anchor" href={item.href} onClick={onClick(item.href)}>
                {item.textContent}
              </a>
            </Item>
          )
        })}
      </List>
      {clas && clas.includes('life') && !isForumPost && <ConvertKitEdm id="336435bc7c" isAmp={isAmp} />}
    </Container>
  )
}
